import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SectionHeading from './SectionHeading';
import TipsterLogo from './TipsterLogo';
import { useData } from '../context/DataContext';
import { recentForm, summarize, cheapestPlan } from '../lib/tips';

export default function TipstersShowcase() {
  const { tipsters, entriesFor } = useData();
  return (
    <section id="tipsters" className="scroll-mt-24 py-24 md:py-32 bg-white" data-testid="tipsters-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <SectionHeading
          eyebrow="The tipsters"
          title="Three tipsters. Three ways to bet. One rule: a single release a day."
          lead="Pick the format that fits you. Each tipster has its own archive, its own pricing and its own discipline."
        />

        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {tipsters.map((t, i) => {
            const list = entriesFor(t.id);
            const s = summarize(list);
            const form = recentForm(list, 10);
            return (
              <div key={t.id} className={`card-lift relative rounded-[28px] border border-line bg-white p-7 flex flex-col ${i === 1 ? 'md:-mt-6' : ''}`} data-testid={`tipster-card-${t.id}`}>
                <div className="h-1.5 w-16 rounded-full mb-6" style={{ background: t.accent }} />
                <div className="flex items-center gap-4">
                  <TipsterLogo tipster={t} className="w-14 h-14" />
                  <div>
                    <h3 className="font-display text-2xl font-bold text-navy">{t.name}</h3>
                    <div className="eyebrow mt-1" style={{ color: t.accent }}>{t.formatLabel}</div>
                  </div>
                </div>
                <p className="mt-5 text-sm text-slate-600 leading-relaxed flex-1">{t.description}</p>

                <div className="mt-6 flex flex-wrap gap-1.5">
                  {t.markets.map(m => <span key={m} className="px-2.5 py-1 rounded-full bg-mist text-slate-700 text-[11px] font-semibold">{m}</span>)}
                </div>

                <div className="mt-6 rounded-2xl bg-navy text-white p-4">
                  <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-wider text-white/50">
                    <span>Last {form.length}</span><span>Record</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <div className="flex gap-1">{form.map((r, j) => <span key={j} className={`w-2.5 h-2.5 rounded-sm ${r === 'WIN' ? 'bg-win' : 'bg-loss'}`} />)}</div>
                    <div className="font-mono text-sm font-bold">{s.won}W – {s.lost}L</div>
                  </div>
                  <div className="mt-2 text-[11px] text-white/60">Win rate {s.winRate}% · avg odds {s.avgOdds}</div>
                </div>

                <div className="mt-6 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[11px] text-slate-500">from</div>
                    <div className="font-mono text-2xl font-bold text-navy">€{cheapestPlan(t).price}<span className="text-xs text-slate-500 font-body font-normal"> / {cheapestPlan(t).days} days</span></div>
                  </div>
                  <Link to={`/tipsters/${t.id}`} className="btn-ghost !h-11 !px-4 text-sm" data-testid={`tipster-card-link-${t.id}`}>Profile <ArrowRight className="w-4 h-4" /></Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
